import { EyeIcon, ShieldIcon } from 'lucide-react';

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  RequestAccess,
  StandingDetails,
  useWorkspaceStanding,
} from '@/features/workspaces';
import { ACCESS_COPY } from '@/features/workspaces/accessCopy';
import { useBandShowsStanding } from '@/shared/components/bandStanding';

/**
 * Whose eyes this workspace is seen through, when they are not a member's (spec 188).
 *
 * Two cases, and silent otherwise: someone reading an Organization workspace they are
 * not in sees "View only"; an org admin in a workspace they have not joined sees
 * "Organization admin" (decision Q7). A chip beside the spend-limit chip rather than a
 * banner, for that chip's reason: on every page, absent almost always, so it is read on
 * the day it appears. The words are in the chip — colour never travels alone — and
 * what they mean is one click away, in a title and two short lines (spec 196 US1).
 *
 * That holds for a page with no band of its own. Where a band shows the standing
 * (`useBandShowsStanding`), the band says it and this chip is not drawn: the same
 * badge twice on one page read as a fault.
 */
export function WorkspaceAccessChip({
  workspaceId,
  workspaceName,
}: {
  workspaceId: string;
  workspaceName: string;
}) {
  const standing = useWorkspaceStanding(workspaceId);
  const bandSaysIt = useBandShowsStanding();
  const kind = standing.readOnly
    ? 'view-only'
    : standing.asOrgAdmin
      ? 'admin'
      : null;
  if (kind === null || bandSaysIt) return null;

  const Icon = kind === 'view-only' ? EyeIcon : ShieldIcon;
  const chip =
    kind === 'view-only' ? ACCESS_COPY.viewOnlyChip : ACCESS_COPY.adminChip;
  const label =
    kind === 'view-only'
      ? ACCESS_COPY.viewOnlyLabel(workspaceName)
      : ACCESS_COPY.adminLabel(workspaceName);

  return (
    <Popover>
      <PopoverTrigger
        aria-label={label}
        className="border-border bg-muted/60 text-foreground hover:bg-accent focus-visible:ring-ring touch-target inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium whitespace-nowrap transition-colors focus-visible:ring-3 focus-visible:outline-none"
      >
        <Icon aria-hidden="true" className="size-3.5" strokeWidth={1.5} />
        {/* Its icon alone on a phone (spec 196 US1): the band below says it in
            words, and the trigger's label says it to a listener. */}
        <span className="max-md:hidden">{chip}</span>
      </PopoverTrigger>
      <PopoverContent align="end" className="flex w-72 flex-col gap-1.5 p-4">
        <StandingDetails kind={kind} workspaceName={workspaceName} />
        {kind === 'view-only' ? (
          <RequestAccess
            workspaceId={workspaceId}
            size="sm"
            compact
            className="mt-2"
          />
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
